import { useEffect, useState } from 'react'
import { apiImage } from '@shop/api-client'

/**
 * صورةٌ محميّةٌ بالجلسة — <b>تُجلب بـ`fetch` لا بـ`src`</b>.
 *
 * رمزُ الوصول في ذاكرة الواجهة ويُرسل في ترويسة `Authorization`، والمتصفّحُ لا
 * يُرسل الترويسةَ مع الصور. والبديلُ مسارٌ عامٌّ بلا جلسة — وصورُ الإدخال
 * تُلتقط للعلب وفيها أرقامُ IMEI تُقرأ بالعين. فتُجلب هنا وتُصنع منها
 * ‏`objectURL`.
 *
 * ⚠ و`URL.revokeObjectURL` ليس تنظيفًا مستحبًّا: كلُّ رابطٍ لا يُبطَل يُبقي
 *   الصورةَ في ذاكرة التبويب حتى يُغلق. وشاشةُ الأصناف تُفتح وتُغلق عشرات
 *   المرّات في اليوم، فتتراكم الميجاباتُ بلا أن يظهر سببٌ في أيّ مكان.
 *
 * ⚠ والحالةُ تُضبط في ردّ النداء وحدَه، ولا تُضبط متزامنةً داخل الأثر: ضبطٌ
 *   متزامنٌ يبدأ رسمةً ثانيةً قبل أن تنتهي الأولى. ولذلك يُحفظ **المسارُ الذي
 *   جاءت منه الصورة** مع الصورة، ويُشتقّ «يُحمَّل» من عدم تطابقه مع المطلوب —
 *   فالقديمةُ لا تظهر لحظةً تحت عنوان الجديدة.
 */
export function useAuthedImage(path: string | null) {
  const [loaded, setLoaded] = useState<{ key: string | null; url: string | null }>({
    key: null,
    url: null,
  })

  useEffect(() => {
    if (path === null) {
      return
    }

    const abort = new AbortController()
    let created: string | null = null

    apiImage(path, abort.signal)
      .then((blob) => {
        if (abort.signal.aborted) {
          return
        }

        created = blob === null ? null : URL.createObjectURL(blob)
        setLoaded({ key: path, url: created })
      })
      .catch(() => {
        if (!abort.signal.aborted) {
          setLoaded({ key: path, url: null })
        }
      })

    return () => {
      abort.abort()

      if (created !== null) {
        URL.revokeObjectURL(created)
      }
    }
  }, [path])

  const fresh = path !== null && loaded.key === path

  return {
    url: fresh ? loaded.url : null,
    loading: path !== null && !fresh,
  }
}
